/**
 * Unified SignalK widget data resolution.
 *
 * Priority (unless forceDemo is enabled on the widget):
 *   1. Live Cerbo stream (fresh timestamp + plugin connected)
 *   2. Database snapshot (signalk_latest) — marked stale/offline when appropriate
 *   3. Demo sample data
 */
import {
  resolveSignalKNode,
  resolveLiveDataPath,
  formatSignalKNumeric,
  normalizeSignalkScalar,
} from './signalKValueFormat';
import { isWidgetForceDemo } from './widgetDemoConfig';

/** Fresh data window — must match Cerbo actively reporting this path. */
export const WIDGET_LIVE_MAX_AGE_MS = 15 * 60 * 1000;

function resolveDatabaseNode(databaseData, databaseFlat, dataPath) {
  const flatEntry = databaseFlat?.[dataPath];
  if (flatEntry && isPresentValue(flatEntry.value)) {
    return {
      rawValue: flatEntry.value,
      signalKUnit: flatEntry.units || flatEntry.unit || '',
      timestamp: flatEntry.timestamp,
      resolvedPath: dataPath,
    };
  }
  return resolveSignalKNode(databaseData, dataPath);
}

function resolveLiveNode(liveData, dataPath) {
  const live = resolveLiveDataPath(liveData, dataPath);
  if (!live?.entry) return null;
  return {
    rawValue: live.entry.value,
    signalKUnit: live.entry.units || live.entry.unit || '',
    timestamp: live.entry.timestamp,
    resolvedPath: live.resolvedPath || dataPath,
  };
}

export const SIGNALK_SOURCE = {
  LIVE: 'live',
  STALE: 'stale',
  OFFLINE: 'offline',
  DATABASE: 'database',
  DEMO: 'demo',
  NONE: 'none',
};

export function dataAgeMs(timestamp, now = Date.now()) {
  if (!timestamp) return Infinity;
  const t = new Date(timestamp).getTime();
  if (Number.isNaN(t)) return Infinity;
  return Math.max(0, now - t);
}

export function isFreshWidgetTimestamp(timestamp, now = Date.now()) {
  return dataAgeMs(timestamp, now) <= WIDGET_LIVE_MAX_AGE_MS;
}

/** Paths that represent device mode/state — blank when not actively reporting. */
export function isDeviceStatusPath(path) {
  if (!path || typeof path !== 'string') return false;
  if (/^electrical\.(chargers|inverters|converter|solar)\./.test(path)) {
    if (/\.(voltage|current|power|panelPower|panelVoltage|yield)/.test(path)) {
      return false;
    }
    return true;
  }
  if (/^electrical\.venus\.(state|acSource)/.test(path)) return true;
  if (/\.(chargingMode|chargingModeNumber|inverterMode|inverterModeNumber|controllerMode|operationMode|loadState|operatingState)$/.test(path)) {
    return true;
  }
  if (/\.leds\./.test(path)) return true;
  if (/\.bms\./.test(path)) return true;
  return false;
}

function classifySourceType(vesselStreamConnected, timestamp, now = Date.now(), { hasValue = false } = {}) {
  const fresh = isFreshWidgetTimestamp(timestamp, now);
  if (vesselStreamConnected && fresh) return SIGNALK_SOURCE.LIVE;
  if (vesselStreamConnected) return SIGNALK_SOURCE.STALE;
  // Cerbo not streaming: still show DB readings as "database", not alarming offline on every tile.
  if (hasValue) return SIGNALK_SOURCE.DATABASE;
  return SIGNALK_SOURCE.OFFLINE;
}

function shouldBlankDeviceStatus(path, sourceType) {
  return isDeviceStatusPath(path) && sourceType !== SIGNALK_SOURCE.LIVE;
}

function isPresentValue(rawValue) {
  const scalar = normalizeSignalkScalar(rawValue);
  if (scalar === null || scalar === undefined) return false;
  if (scalar === '--') return false;
  if (typeof scalar === 'string' && scalar.trim() === '') return false;
  return true;
}

function buildWidgetResult(sourceType, dataPath, resolvedPath, rawValue, signalKUnit, timestamp, widgetConfig) {
  const { value, unit } = formatSignalKNumeric(
    resolvedPath || dataPath,
    rawValue,
    widgetConfig || {},
    signalKUnit
  );
  const lastUpdate = timestamp || null;
  return {
    value,
    unit,
    sourceType,
    lastUpdate,
    resolvedPath: resolvedPath || dataPath,
    isLive: sourceType === SIGNALK_SOURCE.LIVE,
    isStale: sourceType === SIGNALK_SOURCE.STALE,
    isOffline: sourceType === SIGNALK_SOURCE.OFFLINE,
    isDemo: sourceType === SIGNALK_SOURCE.DEMO,
  };
}

const EMPTY_RESULT = {
  value: '--',
  unit: '',
  sourceType: SIGNALK_SOURCE.NONE,
  lastUpdate: null,
  resolvedPath: '',
  isLive: false,
  isStale: false,
  isOffline: false,
  isDemo: false,
};

/**
 * Resolve a widget reading using live → database → demo fallback.
 */
export function resolveWidgetSignalkValue({
  dataPath,
  widgetConfig = {},
  liveData = {},
  wsConnected = false,
  vesselStreamConnected = false,
  databaseData = {},
  databaseFlat = {},
  demoData = {},
  now = Date.now(),
}) {
  if (!dataPath) return { ...EMPTY_RESULT };

  const forceDemo = isWidgetForceDemo(widgetConfig);

  if (forceDemo) {
    const node = resolveSignalKNode(demoData, dataPath);
    if (node && isPresentValue(node.rawValue)) {
      return buildWidgetResult(
        SIGNALK_SOURCE.DEMO,
        dataPath,
        node.resolvedPath || dataPath,
        node.rawValue,
        node.signalKUnit,
        node.timestamp,
        widgetConfig
      );
    }
    return { ...EMPTY_RESULT, unit: widgetConfig.unit || '', resolvedPath: dataPath };
  }

  // Prefer fresh WebSocket readings whenever present. Cerbo stream flag still
  // controls LIVE vs DATABASE badge classification below.
  const cerboLive = Boolean(vesselStreamConnected);
  const wsNode = resolveLiveNode(liveData, dataPath);
  const wsUsable =
    wsNode &&
    isPresentValue(wsNode.rawValue) &&
    (cerboLive || isFreshWidgetTimestamp(wsNode.timestamp, now));

  const dbNode = resolveDatabaseNode(databaseData, databaseFlat, dataPath);

  const node = wsUsable
    ? wsNode
    : dbNode && isPresentValue(dbNode.rawValue)
      ? dbNode
      : null;

  if (node) {
    const usingLive = node === wsNode && (cerboLive || isFreshWidgetTimestamp(node.timestamp, now));
    const sourceType = usingLive && cerboLive
      ? classifySourceType(true, node.timestamp, now, { hasValue: true })
      : classifySourceType(cerboLive, node.timestamp, now, { hasValue: true });

    if (shouldBlankDeviceStatus(dataPath, sourceType) || shouldBlankDeviceStatus(node.resolvedPath, sourceType)) {
      return {
        ...EMPTY_RESULT,
        sourceType,
        lastUpdate: node.timestamp || null,
        resolvedPath: node.resolvedPath || dataPath,
        isStale: sourceType === SIGNALK_SOURCE.STALE,
        isOffline: sourceType === SIGNALK_SOURCE.OFFLINE,
        unit: widgetConfig.unit || '',
      };
    }

    return buildWidgetResult(
      sourceType,
      dataPath,
      node.resolvedPath || dataPath,
      node.rawValue,
      node.signalKUnit,
      node.timestamp,
      widgetConfig
    );
  }

  const sources = [
    { type: SIGNALK_SOURCE.DATABASE, data: databaseData, flatData: databaseFlat },
    { type: SIGNALK_SOURCE.DEMO, data: demoData, flatData: null },
  ];

  for (const source of sources) {
    if (!source.data || typeof source.data !== 'object') continue;
    if (
      Object.keys(source.data).length === 0 &&
      (!source.flatData || Object.keys(source.flatData).length === 0)
    ) {
      continue;
    }

    const fallbackNode = resolveDatabaseNode(source.data, source.flatData, dataPath);
    if (fallbackNode && isPresentValue(fallbackNode.rawValue)) {
      const sourceType =
        source.type === SIGNALK_SOURCE.DEMO
          ? SIGNALK_SOURCE.DEMO
          : classifySourceType(cerboLive, fallbackNode.timestamp, now, { hasValue: true });

      if (
        sourceType !== SIGNALK_SOURCE.DEMO &&
        (shouldBlankDeviceStatus(dataPath, sourceType) ||
          shouldBlankDeviceStatus(fallbackNode.resolvedPath, sourceType))
      ) {
        return {
          ...EMPTY_RESULT,
          sourceType,
          lastUpdate: fallbackNode.timestamp || null,
          resolvedPath: fallbackNode.resolvedPath || dataPath,
          isStale: sourceType === SIGNALK_SOURCE.STALE,
          isOffline: sourceType === SIGNALK_SOURCE.OFFLINE,
          unit: widgetConfig.unit || '',
        };
      }

      return buildWidgetResult(
        sourceType,
        dataPath,
        fallbackNode.resolvedPath,
        fallbackNode.rawValue,
        fallbackNode.signalKUnit,
        fallbackNode.timestamp,
        widgetConfig
      );
    }
  }

  return {
    ...EMPTY_RESULT,
    unit: widgetConfig.unit || '',
    resolvedPath: dataPath,
  };
}

export const SIGNALK_SOURCE_LABELS = {
  [SIGNALK_SOURCE.LIVE]: 'Live',
  [SIGNALK_SOURCE.STALE]: 'Stale',
  [SIGNALK_SOURCE.OFFLINE]: 'Boat offline',
  [SIGNALK_SOURCE.DATABASE]: 'Database',
  [SIGNALK_SOURCE.DEMO]: 'Demo data',
  [SIGNALK_SOURCE.NONE]: 'No data',
};

export const SIGNALK_SOURCE_PRIORITY = [
  SIGNALK_SOURCE.LIVE,
  SIGNALK_SOURCE.DATABASE,
  SIGNALK_SOURCE.DEMO,
];
